import CustomAsyncSelect from "@app/components/custom-async-select";
import { PromiseOptionsCallback } from "@app/components/custom-async-select/custom-async-select.component";
import PaginationMui from "@app/components/pagination-mui/pagination-mui.component";
import { DEBOUNCE_TIMER, DEFAULT_PAGE, OrderType } from "@app/constants";
import StatisticIcon from "@app/icons/dashboard/statistic";
import BookService from "@app/services/http/book.service";
import DashboardService from "@app/services/http/dashboard.service";
import { LabelValue, Meta, StatisticsState } from "@app/types";
import {
  BookPurchase as BookPurchaseType,
  RequestParamsDashboard,
  RequestParamsDashboardType,
} from "@app/types/dashboard.type";
import { Images } from "@assets/images";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import clsx from "clsx";
import { memo, useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";

const headerTitlePurchased = [
  {
    label: "TÊN SÁCH",
    key: "bookName",
    hasOrder: true,
  },
  {
    label: "HÌNH ẢNH",
    key: "thumbnailBook",
    hasOrder: false,
  },
  {
    label: "KHÁCH HÀNG",
    key: "fullName",
    hasOrder: true,
  },
  {
    label: "AVATAR",
    key: "thumbnailUser",
    hasOrder: false,
  },
  {
    label: "NGÀY MUA",
    key: "createDate",
    hasOrder: true,
  },
  {
    label: "GIÁ MUA",
    key: "priceBook",
    hasOrder: true,
  },
  {
    label: "TỔNG THỜI GIAN CỦA SÁCH",
    key: "timeAudioBookLabel",
    hasOrder: true,
  },
  {
    label: "THỜI GIAN ĐÃ NGHE",
    key: "timeUserListenLabel",
    hasOrder: true,
  },
];

const headerTitleViewed = [
  {
    label: "TÊN SÁCH",
    key: "bookName",
    hasOrder: true,
  },
  {
    label: "HÌNH ẢNH",
    key: "thumbnailBook",
    hasOrder: false,
  },
  {
    label: "LƯỢT XEM",
    key: "viewCount",
    hasOrder: true,
  },
  {
    label: "LƯỢT LIKE",
    key: "likeCount",
    hasOrder: true,
  },
  {
    label: "LƯỢT CHIA SẺ",
    key: "shareCount",
    hasOrder: true,
  },
  {
    label: "LƯỢT NGHE",
    key: "listenCount",
    hasOrder: true,
  },
  {
    label: "LƯỢT BÌNH LUẬN",
    key: "commentCount",
    hasOrder: true,
  },
];

const BookPurchase = ({ filter }: { filter: StatisticsState }) => {
  const { abortController } = useCancellationRequest();
  const [data, setData] = useState<BookPurchaseType>();
  const [meta, setMeta] = useState<Meta>();
  const [tab, setTab] = useState(1);
  const [requestParams, setRequestParams] = useState<RequestParamsDashboard>({
    pageNum: DEFAULT_PAGE,
    pageSize: 20,
    type: RequestParamsDashboardType.PURCHASED,
  });
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("");

  useEffect(() => {
    DashboardService.getBookPurchase({
      ...requestParams,
      startDate: filter.startDate || undefined,
      endDate: filter.endDate || undefined,
    }).then((res) => {
      setData(res.data);
      setMeta(res.data.meta);
    });
  }, [requestParams, filter.startDate, filter.endDate]);

  useEffect(() => {
    const newRequestParams: RequestParamsDashboard = {
      ...requestParams,
      sortBy: orderBy,
      sortType: order as OrderType,
    };

    setRequestParams(newRequestParams);
  }, [orderBy, order]);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParamsDashboard = {
      ...requestParams,
      pageNum: page,
    };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParamsDashboard = {
      ...requestParams,
      pageSize,
    };

    setRequestParams(newRequestParams);
  };

  const getBooksOptions = useCallback(
    (inputValue: string, callback: PromiseOptionsCallback) => {
      BookService.getList(
        {
          keyword: inputValue.trim(),
        },
        abortController.signal
      ).then((res) => {
        const data: LabelValue[] = res.data.items
          .map((item) => ({ label: item.name, value: item.id }))
          .filter((item) => {
            if (inputValue.trim()) {
              return (
                item.label
                  .toLowerCase()
                  .normalize("NFD")
                  .replace(/[\u0300-\u036f]/g, "")
                  .indexOf(
                    inputValue
                      .trim()
                      .toLowerCase()
                      .normalize("NFD")
                      .replace(/[\u0300-\u036f]/g, "")
                  ) >= 0
              );
            }

            return true;
          });

        callback(data);
      });
    },
    []
  );

  const onSearchChange = useDebouncedCallback((value: number) => {
    const newRequestParams: RequestParamsDashboard = {
      ...requestParams,
      bookId: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const handleChangeTab = useDebouncedCallback((value: number) => {
    const newRequestParams: RequestParamsDashboard = {
      ...requestParams,
      type: value as RequestParamsDashboardType,
      sortBy: undefined,
      sortType: undefined,
    };

    setRequestParams(newRequestParams);
    setTab(value);
    setOrderBy("");
    setOrder("");
  }, DEBOUNCE_TIMER);

  const handleExportToExcel = () => {
    if (!data?.items) return;

    let excelData;

    if (tab === 1) {
      excelData = [
        [
          "TÊN SÁCH",
          "HÌNH ẢNH",
          "KHÁCH HÀNG",
          "AVATAR",
          "NGÀY MUA",
          "GIÁ MUA",
          "TỔNG THỜI GIAN CỦA SÁCH",
          "THỜI GIAN ĐÃ NGHE",
        ],
        // eslint-disable-next-line no-unsafe-optional-chaining
        ...data?.items.map((item) => [
          item.bookName,
          item.thumbnailBook[0],
          item.fullName,
          item.thumbnailUser,
          item.createDate,
          `${item.priceBook?.toLocaleString("de-DE") ?? 0}đ`,
          item.timeAudioBookLabel,
          item.timeUserListenLabel,
        ]),
      ];
    } else {
      excelData = [
        [
          "Tên",
          "HÌNH ẢNH",
          "LƯỢT XEM",
          "LƯỢT LIKE",
          "LƯỢT CHIA SẺ",
          "LƯỢT NGHE",
          "LƯỢT BÌNH LUẬN",
        ],
        // eslint-disable-next-line no-unsafe-optional-chaining
        ...data?.items.map((item) => [
          item.bookName,
          item.thumbnailBook[0],
          item.viewCount ?? 0,
          item.likeCount ?? 0,
          item.shareCount ?? 0,
          item.listenCount ?? 0,
          item.commentCount ?? 0,
        ]),
      ];
    }

    const worksheet = XLSX.utils.aoa_to_sheet(excelData);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Danh sách");

    XLSX.writeFile(workbook, "Danh_sach.xlsx");
  };

  const handleClickHeader = (item: any) => {
    if (item.hasOrder) {
      const orderBy = item.key;
      const orderType =
        order === OrderType.ASC ? OrderType.DESC : OrderType.ASC;

      setOrderBy(orderBy);
      setOrder(orderType);
    }
  };

  const headers = tab === 1 ? headerTitlePurchased : headerTitleViewed;

  return (
    <div className="p-[24px] bg-white rounded-xl flex flex-col gap-[20px]">
      <div className="flex justify-between items-center">
        <div className="flex gap-1 items-center">
          <StatisticIcon />
          <p className="text-[18px] text-[#45464E] font-medium">
            Thống kê sách
          </p>
        </div>
        <div className="flex gap-2">
          <div className="w-[250px]">
            <CustomAsyncSelect
              promiseOptions={getBooksOptions}
              placeholder="Chọn sách"
              onChange={(e) => e?.value && onSearchChange(+e?.value)}
            />
          </div>
          <button
            type="button"
            disabled={!data?.items?.length}
            className="focus:outline-none text-white bg-green-700 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 disabled:opacity-50"
            onClick={handleExportToExcel}
          >
            Export Excel
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="rounded-[10px] h-[48px] flex w-[320px] border-[1px] border-[#EEF0FA] overflow-hidden">
        <div
          className={clsx(
            "flex-1 text-[14px] font-medium px-[16px] flex items-center justify-center cursor-pointer",
            tab === 1 ? "bg-[#5570F1] text-[#FFF]" : "text-[#6E7079]"
          )}
          onClick={() => handleChangeTab(1)}
        >
          User đã mua sách
        </div>
        <div
          className={clsx(
            "flex-1 text-[14px] font-medium px-[16px] flex items-center justify-center cursor-pointer",
            tab === 2 ? "bg-[#5570F1] text-[#FFF]" : "text-[#6E7079]"
          )}
          onClick={() => handleChangeTab(2)}
        >
          User đã xem sách
        </div>
      </div>

      <div className="relative overflow-x-auto">
        <table className="w-full text-left rtl:text-right">
          <thead>
            <tr className="border-t border-[#EEF0FA]">
              {headers?.map((it) => (
                <th
                  key={it.key}
                  className={clsx(
                    "px-6 py-3 text-[14px] text-[#6E7079] font-normal cursor-pointer",
                    {
                      "bg-[#bac5d4]": it.key === orderBy,
                    }
                  )}
                  onClick={() => handleClickHeader(it)}
                >
                  <div className={"flex items-center"}>
                    {it.label}

                    {it.hasOrder && (!order || it.key !== orderBy) && (
                      <span className="ml-2">
                        <img
                          src={Images.SortIcon.default}
                          alt="sort-icon"
                          className="w-4"
                        />
                      </span>
                    )}

                    {it.hasOrder &&
                      order === OrderType.ASC &&
                      orderBy === it.key && (
                      <div className="ml-2">
                        <img
                          src={Images.ArrowUpIcon.default}
                          alt="arrow-up-icon"
                          className="w-4"
                        />
                      </div>
                    )}

                    {it.hasOrder &&
                      order === OrderType.DESC &&
                      orderBy === it.key && (
                      <div className="ml-2">
                        <img
                          src={Images.ArrowDownIcon.default}
                          alt="arrow-down-icon"
                          className="w-4"
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.items.map((item, index) => (
              <tr
                key={index}
                className="border-t border-[#EEF0FA] text-[14px] text-[#45464E] font-normal"
              >
                <td className="px-6 py-4">
                  <p>{item.bookName}</p>
                </td>
                <td className="px-6 py-4">
                  <img
                    src={item.thumbnailBook[0]}
                    alt="book image"
                    className="w-[32px] h-[32px] rounded-full"
                  />
                </td>
                {tab === 1 && (
                  <>
                    <td className="px-6 py-4">{item.fullName}</td>
                    <td className="px-6 py-4">
                      <img
                        src={item.thumbnailUser}
                        alt="book image"
                        className="w-[32px] h-[32px] rounded-full"
                      />
                    </td>
                    <td className="px-6 py-4">{item.createDate}</td>
                    <td className="px-6 py-4">
                      {item.priceBook &&
                        `${item.priceBook.toLocaleString("de-DE")}đ`}
                    </td>
                    <td className="px-6 py-4">{item.timeAudioBookLabel}</td>
                    <td className="px-6 py-4">{item.timeUserListenLabel}</td>
                  </>
                )}
                {tab === 2 && (
                  <>
                    <td className="px-6 py-4">{item.viewCount ?? 0}</td>
                    <td className="px-6 py-4">{item.likeCount ?? 0}</td>
                    <td className="px-6 py-4">{item.shareCount ?? 0}</td>
                    <td className="px-6 py-4">{item.listenCount ?? 0}</td>
                    <td className="px-6 py-4">{item.commentCount ?? 0}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationMui
        currentPage={meta?.pageNum ?? 1}
        totalPages={meta?.totalPage ?? 1}
        sizePage={meta?.pageSize ?? 1}
        total={meta?.total ?? 0}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
};

export default memo(BookPurchase);
